import { MapPin, Car, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BookingMode } from '../api/use-marketplace';

/**
 * ModeChooser — lets the customer pick how they want the service delivered
 * when 2+ booking modes are available. Auto-hides for single-mode services
 * (no decision to make → don't waste the screen real estate).
 *
 * Marketplace variant: uses react-i18next (the marketplace ships translations
 * in 4 locales). Mirrors the client-booking ModeChooser shape but with
 * lucide icons rather than inline SVG paths, matching the marketplace
 * component idiom.
 */

interface ModeChooserProps {
  availableModes: BookingMode[];
  selectedMode: BookingMode;
  onChange: (mode: BookingMode) => void;
  locationName?: string;
  employeeName?: string;
}

const MODE_CONFIG = {
  AT_LOCATION: { icon: MapPin, labelKey: 'booking.modeChooser.atLocation' },
  MOBILE: { icon: Car, labelKey: 'booking.modeChooser.mobile' },
  ONLINE: { icon: Video, labelKey: 'booking.modeChooser.online' },
} as const;

export function ModeChooser({
  availableModes,
  selectedMode,
  onChange,
  locationName,
  employeeName,
}: ModeChooserProps) {
  const { t } = useTranslation();

  // Single-mode → nothing for the customer to pick. Keep parent render logic
  // simple by absorbing the branch here.
  if (availableModes.length < 2) return null;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {t('booking.modeChooser.title')}
      </h3>
      <div className="grid gap-2">
        {availableModes.map((mode) => {
          const { icon: Icon, labelKey } = MODE_CONFIG[mode];
          const label = t(labelKey);
          // Secondary detail line — gives the row something useful to anchor
          // on when we have richer context (location/employee name).
          const detail =
            mode === 'AT_LOCATION' && locationName
              ? locationName
              : mode === 'MOBILE' && employeeName
                ? employeeName
                : undefined;
          const selected = mode === selectedMode;
          return (
            <button
              key={mode}
              type="button"
              onClick={() => onChange(mode)}
              aria-pressed={selected}
              className={[
                'flex items-center gap-3 p-3 rounded-lg border text-left transition-colors',
                selected
                  ? 'border-foreground bg-foreground/5'
                  : 'border-border/60 hover:border-foreground/30 hover:bg-secondary/40',
              ].join(' ')}
            >
              <Icon
                className={[
                  'w-5 h-5 shrink-0',
                  selected ? 'text-foreground' : 'text-muted-foreground',
                ].join(' ')}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-foreground">{label}</div>
                {detail && (
                  <div className="text-xs text-muted-foreground truncate">{detail}</div>
                )}
              </div>
              {selected && (
                <span
                  aria-hidden="true"
                  className="w-4 h-4 rounded-full bg-foreground flex items-center justify-center shrink-0"
                >
                  <span className="w-2 h-2 rounded-full bg-background" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
